/**
 * Loads marketing scripts (e.g. ads, retargeting pixels) only when the user has
 * consented via the cookie banner. Respects GDPR/CCPA preferences.
 *
 * Add env vars (e.g. REACT_APP_FB_PIXEL_ID) and extend the effect below
 * to load marketing scripts when configured.
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  mayLoadMarketing,
  getStoredPreferences,
  COOKIE_PREFERENCES_CHANGED,
} from '../utils/cookieConsent';

// Example: process.env.REACT_APP_FB_PIXEL_ID
const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID;

export const ConditionalMarketing: React.FC = () => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    const checkAndLoad = () => {
      if (!mayLoadMarketing()) return;
      if (loadedRef.current) return;

      loadedRef.current = true;
      setShouldLoad(true);
    };

    checkAndLoad();

    const onPreferencesChanged = () => {
      const prefs = getStoredPreferences();
      if (prefs?.marketing) {
        checkAndLoad();
      }
    };

    window.addEventListener(COOKIE_PREFERENCES_CHANGED, onPreferencesChanged);
    return () =>
      window.removeEventListener(
        COOKIE_PREFERENCES_CHANGED,
        onPreferencesChanged
      );
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;

    // Add marketing scripts here when configured.
    // Example: Facebook Pixel
    if (FB_PIXEL_ID) {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
        document,'script','https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${FB_PIXEL_ID}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(script);
    }
  }, [shouldLoad]);

  return null;
};

export default ConditionalMarketing;
